import './search-featured-list.scss'

import { pluck, tap } from 'rxjs/operators'
import { defineComponent, PropType } from 'vue'

import { SearchResponse, SearchResult, SearchFeatured, SearchQuery, SearchParams, Wemi } from '@skinsoft/types'
import { map } from '@skinsoft/data-store'
import { WemiDataStore } from '@skinsoft/entity'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { CreateReportSearchFeaturedCard } from '@skinsoft/entity-ui'
import { toSearchFeatured$ } from '@skinsoft/search-data-store/src/featured'
import { NB_ITEMS_SEARCH_RESULT_CHANGED_TOPIC } from '@skinsoft/bus'

const SEARCH_FEATURED_ITEM_COUNT = 5;

export const SearchFeaturedList = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sCreateReportSearchFeaturedCard: CreateReportSearchFeaturedCard,
    },
    props: {
        emptyMessage: {},
        title: {},
        targetRouteName: {},
        searchParams: { type: Object as PropType<SearchParams>, required: true },
        displayedButtons: {},
        titleClickAction: {},
        onlyInCache: {
            type: Boolean,
            default: false,
        },
        onlyUploaded: {
            type: Boolean,
            default: false,
        },
    },
    template: require('./search-featured-list.html'),
    data() {
        return {
            // TODO make it generic to keep one component ?
            searchFeatureds: [] as SearchFeatured[],
            totalResults: 0,
        }
    },
    mounted() {
        this.unsubscribe()
        this.subscribe()
    },
    watch: {
        searchParams: {
            handler() {
                // Testing all search params presence to avoid useless search query during params setting
                if (this.searchParams.nbResults && this.searchParams.sortType?.length) {
                    this.unsubscribe();
                    this.searchFeatureds = Array(SEARCH_FEATURED_ITEM_COUNT).fill(null);
                    this.$emit('totalCount', null);
                    this.subscribe();
                }
            },
            immediate: false,
            deep: true,
        },
    },
    methods: {
        subscribe() {
            const locale = this.getAppLocale()
            const sortObject =
                this.searchParams?.sortType?.length > 0
                    ? { entityFields: this.searchParams.sortType, sortAsc: this.searchParams.sortAsc }
                    : null

            if (this.onlyInCache || !window.navigator.onLine) {
                const searchQuery: SearchQuery = {
                    queryText: this.searchParams.queryText,
                    sortObject: sortObject,
                    pageSize: this.searchParams.nbResults,
                }

                this.subscribeTo(
                    this.$itemDataStore
                    .searchAllOffline(
                        {
                            queryText: this.searchParams.queryText,
                            sortObject: sortObject,
                            pageSize: this.searchParams.nbResults,
                            language: this.getAppLocale(),
                        },
                        undefined,
                        undefined,
                        undefined,
                        this.onlyInCache,
                    )
                    .pipe(
                        tap((result: SearchResponse<SearchResult | Wemi>) => { this.totalResults = result.totalSize}),
                        pluck<SearchResponse<SearchResult | Wemi>, SearchResult[]>('allData'),
                        map((value: SearchResult) => {
                            return toSearchFeatured$(value, locale)
                        })
                    ),
                    (searchFeatureds: SearchFeatured[]) => {
                        this.searchFeatureds = searchFeatureds;
                        this.emitOnBus(NB_ITEMS_SEARCH_RESULT_CHANGED_TOPIC, { nbResults: searchFeatureds.length, totalResults: this.totalResults})
                    },
                    'searchFeatureds'
                )
            } else {

                this.subscribeTo(
                    WemiDataStore.getInstance()
                        .searchAll(
                            {
                                queryText: this.searchParams.queryText,
                                sortObject: sortObject,
                                pageSize: this.searchParams.nbResults,
                                language: this.getAppLocale(),
                            },
                            undefined,
                            undefined,
                            undefined,
                            this.onlyInCache,
                        )
                        .pipe(
                            tap((result: SearchResponse<SearchResult | Wemi>) => { this.totalResults = result.totalSize }),
                            pluck<SearchResponse<SearchResult | Wemi>, SearchResult[]>('allData'),
                            map((value: SearchResult) => {
                                return toSearchFeatured$(value, locale)
                            })
                        ),
                    (searchFeatureds: SearchFeatured[]) => {
                        if (this.onlyUploaded) {
                            this.searchFeatureds = searchFeatureds.filter(e => e.itemId.startsWith('_'))
                        } else {
                            this.searchFeatureds = searchFeatureds
                        }
                        this.emitOnBus(NB_ITEMS_SEARCH_RESULT_CHANGED_TOPIC, { nbResults: searchFeatureds.length, totalResults: this.totalResults });
                    },
                    'searchFeatureds',
                );
            }
        },
        unsubscribe() {
            this.unsubscribeFrom('searchFeatureds');
        },
    },
})
